import type { Match } from '../api/types';

export interface DisplayScore {
  /** Main number to show as the big digits. */
  mainHome: number;
  mainAway: number;
  /** Latest phase the main score represents. */
  phase: 'regular' | 'extraTime';
  hadExtraTime: boolean;
  hadPenalties: boolean;
  /** Long-form annotation for big surfaces (card, BetPage). Null when there is nothing to add. */
  annotationVerbose: string | null;
  /** Short annotation for tight surfaces (timeline, table, pill). */
  annotationShort: string | null;
  /** Same as annotationShort, but each phase on its own line — for very narrow cells. */
  annotationShortLines: string[];
}

/**
 * Picks the score to show prominently for a finished match and builds the
 * supplementary annotation. Rules:
 *  - Main = extra-time score when present, otherwise regulation score.
 *    Penalty shootout result is NEVER the main score (it's not "goals in play").
 *  - Annotation lists the other phases that happened, in order: regulation first,
 *    then penalty shootout. When neither extra time nor penalties happened the
 *    annotation is null.
 *
 * Returns null when the match has no regulation score yet (not finished or no data).
 */
export function pickDisplayScore(m: Match): DisplayScore | null {
  if (m.homeScore == null || m.awayScore == null) return null;

  const hadExtraTime = m.extraTimeHomeScore != null && m.extraTimeAwayScore != null;
  const hadPenalties = m.penaltyHomeScore != null && m.penaltyAwayScore != null;

  const mainHome = hadExtraTime ? (m.extraTimeHomeScore as number) : m.homeScore;
  const mainAway = hadExtraTime ? (m.extraTimeAwayScore as number) : m.awayScore;

  const verboseParts: string[] = [];
  const shortParts: string[] = [];

  if (hadExtraTime) {
    verboseParts.push(`w 90' ${m.homeScore}-${m.awayScore}`);
    shortParts.push(`${m.homeScore}-${m.awayScore} w 90'`);
  }
  if (hadPenalties) {
    const p = `${m.penaltyHomeScore}-${m.penaltyAwayScore}`;
    verboseParts.push(`karne ${p}`);
    shortParts.push(`${p} k.`);
  }

  return {
    mainHome,
    mainAway,
    phase: hadExtraTime ? 'extraTime' : 'regular',
    hadExtraTime,
    hadPenalties,
    annotationVerbose: verboseParts.length ? verboseParts.join(' · ') : null,
    annotationShort: shortParts.length ? shortParts.join(' · ') : null,
    annotationShortLines: shortParts,
  };
}
